import { useState, useContext } from "react";
import Markdown from "markdown-to-jsx";
import { DataContext } from "../../context/DataContext";
import styles from "./ESPWellness.module.css";
import Button from "../../shared/Button/Button";
import Heading from "../../shared/Heading/Heading";
import InputCard from "../../components/InputCard/InputCard";
import AxiosInstance from "../../url/createAxios";
import LoadingContainer from "../../shared/LoadingContainer/LoadingContainer";

const LOADING_PAGE_HTML = `
  <html>
    <head>
      <title>Loading...</title>
      <style>
        body {
          margin: 0;
          padding: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          background-color: #f5f5f5;
        }
        .loader-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .loader-svg {
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    </head>
    <body>
      <div class="loader-container">
        <svg class="loader-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50.78 50.78" style="fill: #0F3448;">
          <g><g><path d="M7.89,28.7A17.81,17.81,0,0,1,19.55,8.55,17.58,17.58,0,0,1,28.8,7.9a2.83,2.83,0,0,1,.4.08l1.07-1.56,3.47-5A25.38,25.38,0,0,0,.42,30ZM31.23,42.23a17.64,17.64,0,0,1-7.63.88,1,1,0,0,1-.24,0h0l-.24.5-3.19,6.58A25.39,25.39,0,0,0,50.3,20.54l-7.13,3.63A17.84,17.84,0,0,1,31.23,42.23Z"></path></g></g>
        </svg>
      </div>
    </body>
  </html>
`;

const ESPWellness = () => {
  const instance = AxiosInstance();
  const [isChecked, setIsChecked] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [redirectURL, setRedirectURL] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { ESPSpaPage, reservation } = useContext(DataContext);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const handleEmailChange = (_, value) => {
    setEmailValue(value);
    setIsEmailValid(emailRegex.test(value));
  };

  const handleCheckBox = () => {
    setIsChecked(!isChecked);
  }

  const isButtonEnabled = () => {
    return !isLoading && (reservation.EmailAddress || (isChecked && isEmailValid));
  }

  const isSafari = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.indexOf('safari') !== -1 && userAgent.indexOf('chrome') === -1;
  }

  const handleBookButton = async () => {
    setIsLoading(true);

    const safari = isSafari();

    let newWindow = null;
    if (safari) {
      newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(LOADING_PAGE_HTML);
      }
    }

    try {
      const { data } = await instance.post(
        "/esp-spa-page/ESPRedirectURL/",
        {emailAddress: emailValue}, 
        {withCredentials: true});
    
        setRedirectURL(data.redirectURL);
    
        if (safari && newWindow && !newWindow.closed) {
          newWindow.location.href = data.redirectURL;
        } else {
          window.open(data.redirectURL, '_blank');
        }
    } catch (e) {
        console.error(e.message);
        if (safari && newWindow && !newWindow.closed) {
          newWindow.close();
        }
    }

    setIsLoading(false);
  };

  return (
    <>
      <div className={styles.pageWrapper}>
        <section className={styles.container}>
            <div 
              className={styles.contentWrapper}
            >
              <div 
                className={styles.image}
                style={{
                  backgroundImage: `url(${ESPSpaPage.Image?.formats?.medium?.url || ESPSpaPage.Image?.url})`,
                }}
              ></div>
              <div className={styles.content}>
                <Heading
                  txt={ESPSpaPage.Title}
                  paddingTop={"0px"}
                  paddingBottom={"20px"}
                  fontSize={"24px"}
                  fontFamily="Georgia"
                  textTransform="uppercase"
                />
                {ESPSpaPage.Description &&
                  <Markdown>
                    {ESPSpaPage.Description}
                  </Markdown>
                }
                {!isLoading && !redirectURL && ESPSpaPage.EmailInfoText && !reservation.EmailAddress &&
                  <p>{ESPSpaPage.EmailInfoText}</p>
                }
                {!isLoading && !redirectURL && !reservation.EmailAddress &&
                  <InputCard
                    newBoxStyles={{
                      height: "30px",
                      border: "1px solid var(--color-primary)",
                      width: "100%",
                      maxWidth: "350px",
                      marginTop: "10px",
                    }}
                    name={"email"}
                    setFormData={(key, value) => handleEmailChange(key, value)}
                    value={emailValue}
                  />
                }
                {!isLoading && !redirectURL && !reservation.EmailAddress &&
                <div className={styles.checkBoxWrapper}>
                  <div className={styles.checkBox} onClick={handleCheckBox}>
                    {isChecked &&
                      <img src={"/img/info_icons/done_check.svg"} alt={"checked"} />
                    }
                  </div>
                  <Markdown>
                    {ESPSpaPage.ConsentText}
                  </Markdown>
                </div>
                }
                {ESPSpaPage.ButtonText &&
                  <div style={{ marginTop: "25px", position: "relative" }}>
                    <Button
                      txt={ESPSpaPage.ButtonText}
                      backgroundColor={"var(--color-primary)"}
                      color={"var(--color-secondary)"}
                      minHeight="50px"
                      onClick={handleBookButton}
                      newStyles={{
                        paddingBlock: "10px",
                        opacity: isButtonEnabled()
                          ? "1"
                          : "0.4",
                        pointerEvents: isButtonEnabled()
                          ? "auto"
                          : "none",
                      }}
                    />
                    {isLoading &&
                      <LoadingContainer 
                        loaderSize={"30px"} 
                        loaderWrapperStyle={{ 
                          justifyContent: "start",
                          marginLeft: "-16px",
                          position: "absolute"
                        }}
                      />
                    }
                  </div>
                }
              </div>
            </div>
        </section>
      </div>
    </>
  );
};

export default ESPWellness;
